/**
 * Reserve modal. Step 1 is name and email.
 * Without a plan, submit ends on the thank-you panel.
 * Core and Core+ continue to Stripe. The intro-call calendar loads only
 * after that payment, from the server, so the page HTML has no booking URL.
 */
import Tracking from './tracking.js';
import { TRACKING_EVENTS } from './tracking-constants.js';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Where people land after closing the thank-you panel.
const ARTICLES_URL = 'https://psystandard.com/category/articles/';
const FADE_OUT_MS = 240;

const PLAN_TITLES = {
    core: 'Reserve your place in Core',
    'core-plus': 'Reserve your place in Core+',
};

const PLAN_HASHES = {
    '#join-core': 'core',
    '#join-core-plus': 'core-plus',
    '#membership-core': 'core',
    '#membership-core-plus': 'core-plus',
};

const ReserveModal = {
    init() {
        const modal = document.querySelector('[data-reserve-modal]');
        if (!modal || modal.dataset.reserveBound === 'true') {
            return;
        }
        modal.dataset.reserveBound = 'true';

        const dialog = modal.querySelector('[data-reserve-dialog]');
        const form = modal.querySelector('#reserve-form');
        const nameField = modal.querySelector('[data-reserve-name]');
        const emailField = modal.querySelector('[data-reserve-email]');
        const title = modal.querySelector('[data-reserve-title]');
        const stepLabel = modal.querySelector('[data-reserve-step-label]');
        const message = modal.querySelector('[data-reserve-message]');
        const continueLabel = modal.querySelector('[data-reserve-continue-label]');
        const continueBtn = modal.querySelector('[data-reserve-continue]');
        const frame = modal.querySelector('[data-reserve-frame]');
        const variantField = modal.querySelector('[data-reserve-variant]');
        const planField = modal.querySelector('[data-reserve-plan]');
        const billingField = modal.querySelector('[data-reserve-billing]');
        const sourceField = modal.querySelector('[data-reserve-source-field]');
        const continueDefault = continueLabel ? continueLabel.textContent : 'Continue';

        const joinCfg = () => window.pe_mp_join_form || {};
        let turnstileWidget = null;
        let turnstileLoader = null;
        let turnstilePending = false;
        let turnstileBypass = false;
        let tokenReady = Promise.resolve();
        let submitting = false;
        let submittedKey = '';
        let step2Tracked = false;
        let lastFocus = null;
        let context = { plan: '', billing: '', source: 'other' };
        let stepTotal = 2;
        let currentStep = 1;
        let done = false;
        const donePanel = modal.querySelector('[data-reserve-done]');
        const bookedBtn = modal.querySelector('[data-reserve-booked]');

        const ajaxUrl = () => joinCfg().ajax_url
            || (window.pe_mp_form_ajax && window.pe_mp_form_ajax.ajax_url)
            || '/wp-admin/admin-ajax.php';

        const variantFor = (plan) => {
            if (plan === 'core') {
                return 'reserve-core';
            }
            if (plan === 'core-plus') {
                return 'reserve-core-plus';
            }
            return 'reserve-general';
        };

        const applyContext = (plan, billing, source) => {
            context = {
                plan: plan === 'core' || plan === 'core-plus' ? plan : '',
                billing: billing === 'monthly' || billing === 'yearly' ? billing : '',
                source: source || 'other',
            };
            if (context.plan === '') {
                context.billing = '';
            }
            if (planField) {
                planField.value = context.plan;
            }
            if (billingField) {
                billingField.value = context.billing;
            }
            if (variantField) {
                variantField.value = variantFor(context.plan);
            }
            if (sourceField) {
                sourceField.value = context.source;
            }
            if (title) {
                title.textContent = PLAN_TITLES[context.plan] || 'Reserve your place';
            }
            const needsCountry = context.plan === 'core' || context.plan === 'core-plus';
            const countryWrap = modal.querySelector('[data-reserve-country-field]');
            const countryField = modal.querySelector('[data-reserve-country]');
            if (countryWrap) {
                countryWrap.hidden = !needsCountry;
            }
            if (countryField) {
                countryField.required = needsCountry;
                if (!needsCountry) {
                    countryField.value = '';
                    countryField.classList.remove('is-invalid');
                }
            }
        };

        const hideMessage = () => {
            if (!message) {
                return;
            }
            message.hidden = true;
            message.textContent = '';
        };

        const showMessage = (text) => {
            if (!message) {
                return;
            }
            message.hidden = false;
            message.textContent = text;
        };

        const clearInvalid = () => {
            form?.querySelectorAll('.is-invalid').forEach((field) => {
                field.classList.remove('is-invalid');
            });
        };

        const markInvalid = (field, invalid) => {
            if (!field) {
                return;
            }
            field.classList.toggle('is-invalid', invalid);
            field.setAttribute('aria-invalid', invalid ? 'true' : 'false');
        };

        const countryValue = () => {
            const countryField = modal.querySelector('[data-reserve-country]');
            return (countryField?.value || '').trim().toUpperCase();
        };

        const validate = () => {
            const nameOk = Boolean(nameField && nameField.value.trim());
            const emailOk = Boolean(emailField && EMAIL_REGEX.test(emailField.value.trim()));
            const countryField = modal.querySelector('[data-reserve-country]');
            const countryOk = !usesStripeStep() || /^[A-Z]{2}$/.test(countryValue());
            markInvalid(nameField, !nameOk);
            markInvalid(emailField, !emailOk);
            markInvalid(countryField, !countryOk);
            if (!nameOk && nameField) {
                nameField.focus();
            } else if (!emailOk && emailField) {
                emailField.focus();
            } else if (!countryOk && countryField) {
                countryField.focus();
            }
            return nameOk && emailOk && countryOk;
        };

        const payloadKey = () => [
            nameField?.value.trim() || '',
            emailField?.value.trim().toLowerCase() || '',
            context.plan,
            context.billing,
            countryValue(),
        ].join('|');

        const mintTimeTrap = () => {
            if (!form) {
                return Promise.resolve();
            }
            const body = new FormData();
            body.append('action', 'pe_mp_join_form_token');
            tokenReady = fetch(ajaxUrl(), {
                method: 'POST',
                body,
                credentials: 'same-origin',
                headers: { 'X-Requested-With': 'XMLHttpRequest' },
            })
                .then((response) => response.json())
                .then((data) => {
                    if (!data || !data.success || !data.data) {
                        return;
                    }
                    const ts = form.querySelector('[name="form_ts"]');
                    const sig = form.querySelector('[name="form_sig"]');
                    if (ts) {
                        ts.value = data.data.form_ts || '';
                    }
                    if (sig) {
                        sig.value = data.data.form_sig || '';
                    }
                })
                .catch(() => {});
            return tokenReady;
        };

        const loadTurnstile = () => {
            const siteKey = joinCfg().turnstile_site_key;
            if (!siteKey) {
                return Promise.resolve();
            }
            if (window.turnstile) {
                return Promise.resolve();
            }
            if (turnstileLoader) {
                return turnstileLoader;
            }
            turnstileLoader = new Promise((resolve) => {
                const script = document.createElement('script');
                script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
                script.async = true;
                script.onload = () => resolve();
                script.onerror = () => resolve();
                document.head.appendChild(script);
            });
            return turnstileLoader;
        };

        const renderTurnstile = () => {
            const siteKey = joinCfg().turnstile_site_key;
            if (!form || !siteKey || !window.turnstile) {
                return;
            }
            const slot = form.querySelector('[data-reserve-turnstile]');
            if (!slot) {
                return;
            }
            if (turnstileWidget !== null) {
                window.turnstile.reset(turnstileWidget);
                return;
            }
            turnstileWidget = window.turnstile.render(slot, {
                sitekey: siteKey,
                size: 'invisible',
                execution: 'render',
                callback: (token) => {
                    const waiting = turnstilePending;
                    turnstilePending = false;
                    let input = form.querySelector('[name="cf-turnstile-response"]');
                    if (!input) {
                        input = document.createElement('input');
                        input.type = 'hidden';
                        input.name = 'cf-turnstile-response';
                        form.appendChild(input);
                    }
                    if (token) {
                        input.value = token;
                    }
                    if (waiting && typeof form.requestSubmit === 'function') {
                        form.requestSubmit();
                    }
                },
                'error-callback': () => {
                    const waiting = turnstilePending;
                    turnstilePending = false;
                    if (!waiting) {
                        return;
                    }
                    turnstileBypass = true;
                    if (typeof form.requestSubmit === 'function') {
                        form.requestSubmit();
                    }
                },
                'expired-callback': () => {
                    const input = form.querySelector('[name="cf-turnstile-response"]');
                    if (input) {
                        input.value = '';
                    }
                },
            });
        };

        const clearTurnstileToken = () => {
            const input = form?.querySelector('[name="cf-turnstile-response"]');
            if (input) {
                input.value = '';
            }
            if (turnstileWidget !== null && window.turnstile) {
                window.turnstile.reset(turnstileWidget);
            }
        };

        const loadFrame = (embed) => {
            if (!frame || !embed || frame.getAttribute('src') === embed) {
                return;
            }
            frame.setAttribute('src', embed);
        };

        const paymentIntentFromLocation = () => {
            const fromSearch = new URLSearchParams(window.location.search).get('payment_intent') || '';
            if (/^pi_[A-Za-z0-9]+$/.test(fromSearch)) {
                return fromSearch;
            }
            const match = window.location.hash.match(/payment_intent=(pi_[A-Za-z0-9]+)/);
            return match ? match[1] : '';
        };

        const showScheduleUnavailable = () => {
            modal.querySelector('[data-reserve-sched]')?.setAttribute('hidden', '');
            modal.querySelector('[data-reserve-fallback]')?.setAttribute('hidden', '');
            const note = modal.querySelector('[data-reserve-schedule-unavailable]');
            if (note) {
                note.hidden = false;
            }
        };

        const showPaidSchedule = (schedule) => {
            const embed = String(schedule?.embed || '');
            const link = String(schedule?.link || '');
            if (!embed) {
                showScheduleUnavailable();
                return;
            }
            const sched = modal.querySelector('[data-reserve-sched]');
            if (sched) {
                sched.hidden = false;
            }
            loadFrame(embed);
            const fallback = modal.querySelector('[data-reserve-fallback]');
            const anchor = modal.querySelector('[data-reserve-schedule-link]');
            if (fallback && anchor && link) {
                anchor.href = link;
                fallback.hidden = false;
            }
        };

        const loadPaidSchedule = async (paymentIntentId) => {
            const config = window.pe_mp_stripe || {};
            const id = String(paymentIntentId || paymentIntentFromLocation() || '').trim();
            if (!config.scheduleUrl || !config.nonce || !/^pi_[A-Za-z0-9]+$/.test(id)) {
                showScheduleUnavailable();
                return;
            }
            try {
                const response = await fetch(config.scheduleUrl, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-Requested-With': 'XMLHttpRequest',
                        ...(config.restNonce ? { 'X-WP-Nonce': config.restNonce } : {}),
                    },
                    body: JSON.stringify({
                        nonce: config.nonce,
                        payment_intent_id: id,
                    }),
                });
                const payload = await response.json().catch(() => ({}));
                if (!response.ok || !payload?.embed) {
                    showScheduleUnavailable();
                    return;
                }
                showPaidSchedule(payload);
            } catch (error) {
                showScheduleUnavailable();
            }
        };

        const clearFrame = () => {
            if (frame) {
                frame.removeAttribute('src');
            }
        };

        const syncStepTotal = (total) => {
            stepTotal = total === 3 ? 3 : 2;
            modal.querySelector('[data-reserve-progress-wrap]')?.classList.toggle('is-three', stepTotal === 3);
            const extra = modal.querySelector('[data-reserve-progress="3"]');
            if (extra) {
                extra.hidden = stepTotal !== 3;
            }
        };

        // Fade the dialog out, swap the content, and let the CSS fade the new panel in.
        let leaving = false;
        const transition = (swap) => {
            const reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
            if (!dialog || reduced || !modal.classList.contains('is-open')) {
                swap();
                return;
            }
            leaving = true;
            dialog.classList.add('is-leaving');
            window.setTimeout(() => {
                swap();
                leaving = false;
                dialog.classList.remove('is-leaving');
            }, FADE_OUT_MS);
        };

        const setDone = (isDone) => {
            done = isDone;
            modal.classList.toggle('is-done', isDone);
            dialog?.classList.toggle('is-done', isDone);
            if (donePanel) {
                donePanel.hidden = !isDone;
            }
        };

        // Google's booking iframe is cross-origin and reports nothing back,
        // so the thank-you panel opens from the "booked" button or on close.
        const showDone = (trigger) => {
            if (done || leaving) {
                return;
            }
            transition(() => renderDone(trigger));
        };

        const doneTitle = modal.querySelector('.reserve-modal__done-title');
        const doneText = modal.querySelector('.reserve-modal__done-text');
        const doneTitleDefault = doneTitle ? doneTitle.innerHTML : '';
        const doneTextDefault = doneText ? doneText.innerHTML : '';

        const renderDone = (trigger) => {
            setDone(true);
            if (doneTitle && doneText) {
                if (trigger === 'lead') {
                    doneTitle.textContent = 'Thanks. Next steps come by email.';
                    doneText.textContent = 'The intro call is booked after payment, from the link in that email.';
                } else {
                    doneTitle.innerHTML = doneTitleDefault;
                    doneText.innerHTML = doneTextDefault;
                }
            }
            modal.querySelectorAll('[data-reserve-step]').forEach((panel) => {
                panel.hidden = true;
            });
            if (dialog) {
                dialog.classList.remove('is-wide');
                dialog.setAttribute('aria-labelledby', 'reserve-modal-done-title');
            }
            clearFrame();
            modal.scrollTop = 0;
            modal.querySelector('#reserve-modal-done-title')?.focus();
            Tracking.trackStandardEvent(TRACKING_EVENTS.RESERVE.THANK_YOU_VIEW, {
                source: context.source,
                plan: context.plan || 'none',
                trigger,
            });
        };

        const showStep = (step, animate = false) => {
            if (animate) {
                if (!leaving) {
                    transition(() => showStep(step));
                }
                return;
            }
            const showingCalendar = step > 1;
            const current = Math.min(step, stepTotal);
            currentStep = current;
            setDone(false);
            if (bookedBtn) {
                bookedBtn.hidden = !(showingCalendar && stepTotal === 3);
            }
            modal.querySelectorAll('[data-reserve-step]').forEach((panel) => {
                const panelStep = Number(panel.getAttribute('data-reserve-step'));
                panel.hidden = showingCalendar ? panelStep !== 2 : panelStep !== 1;
            });
            modal.querySelectorAll('[data-reserve-progress]').forEach((bar) => {
                bar.classList.toggle('is-on', Number(bar.getAttribute('data-reserve-progress')) <= current);
            });
            const back = modal.querySelector('[data-reserve-back]');
            if (back) {
                back.hidden = showingCalendar && stepTotal === 3;
            }
            if (dialog) {
                dialog.classList.toggle('is-wide', showingCalendar);
                dialog.setAttribute('aria-labelledby', showingCalendar ? 'reserve-modal-step2-title' : 'reserve-modal-title');
            }
            if (stepLabel) {
                stepLabel.textContent = `Step ${current} of ${stepTotal}`;
            }
            modal.scrollTop = 0;
            if (showingCalendar) {
                if (!step2Tracked) {
                    step2Tracked = true;
                    Tracking.trackStandardEvent(TRACKING_EVENTS.RESERVE.STEP2_VIEW, {
                        source: context.source,
                        plan: context.plan || 'none',
                        billing: context.billing || 'none',
                        step: current,
                    });
                }
                const heading = modal.querySelector('#reserve-modal-step2-title');
                if (heading) {
                    heading.setAttribute('tabindex', '-1');
                    heading.focus();
                }
            }
        };

        const showIntroCall = (paymentIntentId) => {
            step2Tracked = false;
            syncStepTotal(3);
            modal.hidden = false;
            modal.classList.add('is-open');
            document.body.style.overflow = 'hidden';
            closeOffCanvas();
            showStep(3);
            loadPaidSchedule(paymentIntentId);
        };

        const closeOffCanvas = () => {
            const offCanvas = document.querySelector('.off-canvas--menu');
            if (offCanvas?.classList.contains('off-canvas--visible')) {
                offCanvas.classList.remove('off-canvas--visible');
                document.body.classList.remove('off-canvas-menu-open', 'off-canvas-opened-by-search');
            }
        };

        const sourceFrom = (trigger) => {
            const explicit = trigger.getAttribute('data-reserve-source');
            if (explicit) {
                return explicit;
            }
            if (trigger.closest('.off-canvas')) {
                return 'menu';
            }
            if (trigger.closest('header, .header, .header-new')) {
                return 'header';
            }
            if (trigger.closest('#subscription, .membership-v2__pricing')) {
                return 'pricing';
            }
            if (trigger.closest('.membership-v2__hero, .home-main')) {
                return 'hero';
            }
            return 'other';
        };

        const open = ({ plan = '', billing = '', source = 'other' } = {}) => {
            lastFocus = document.activeElement;
            submittedKey = '';
            step2Tracked = false;
            submitting = false;
            if (form) {
                form.reset();
            }
            clearInvalid();
            hideMessage();
            if (continueLabel) {
                continueLabel.textContent = continueDefault;
            }
            if (continueBtn) {
                continueBtn.disabled = false;
            }
            applyContext(plan, billing, source);
            syncStepTotal(usesStripeStep() ? 3 : 2);
            showStep(1);
            modal.hidden = false;
            modal.classList.add('is-open');
            document.body.style.overflow = 'hidden';
            closeOffCanvas();
            mintTimeTrap();
            loadTurnstile().then(renderTurnstile);
            Tracking.trackStandardEvent(TRACKING_EVENTS.RESERVE.OPEN, {
                source: context.source,
                plan: context.plan || 'none',
                billing: context.billing || 'none',
            });
            window.setTimeout(() => {
                if (nameField) {
                    nameField.focus();
                }
            }, 0);
        };

        const close = ({ restoreFocus = true } = {}) => {
            modal.classList.remove('is-open');
            modal.hidden = true;
            document.body.style.overflow = '';
            clearFrame();
            showStep(1);
            if (restoreFocus && lastFocus && typeof lastFocus.focus === 'function') {
                lastFocus.focus();
            }
        };

        // Paid members closing the call step have finished the flow: thank them first.
        const requestClose = () => {
            if (leaving) {
                return;
            }
            if (!done && stepTotal === 3 && currentStep > 1) {
                showDone('close');
                return;
            }
            if (done) {
                // Thank-you panel dismissed: send people on to the articles.
                close({ restoreFocus: false });
                window.location.assign(ARTICLES_URL);
                return;
            }
            close();
        };

        const usesStripeStep = () => context.plan === 'core' || context.plan === 'core-plus';

        const openStripeStep = () => {
            if (typeof window.openMembershipJoinModal !== 'function') {
                showMessage('Card checkout is still loading. Please try Continue again.');
                return false;
            }
            const details = {
                step: 2,
                prefilled: true,
                billing: context.billing || 'monthly',
                name: nameField?.value.trim() || '',
                email: emailField?.value.trim() || '',
                country: countryValue(),
            };
            const plan = context.plan;
            close({ restoreFocus: false });
            window.openMembershipJoinModal(plan, details);
            return true;
        };

        const setBusy = (busy) => {
            if (!continueBtn || !continueLabel) {
                return;
            }
            continueBtn.disabled = busy;
            continueLabel.textContent = busy ? 'Sending…' : continueDefault;
        };

        const trackSubmit = (success) => {
            Tracking.trackStandardEvent(TRACKING_EVENTS.RESERVE.STEP1_SUBMIT, {
                source: context.source,
                plan: context.plan || 'none',
                billing: context.billing || 'none',
                success,
            });
        };

        if (form) {
            form.addEventListener('submit', (event) => {
                const siteKey = joinCfg().turnstile_site_key;
                if (!siteKey || turnstileWidget === null || !window.turnstile) {
                    return;
                }
                if (turnstileBypass) {
                    turnstileBypass = false;
                    return;
                }
                const token = form.querySelector('[name="cf-turnstile-response"]');
                if (token && token.value) {
                    return;
                }
                event.preventDefault();
                event.stopImmediatePropagation();
                if (turnstilePending) {
                    return;
                }
                turnstilePending = true;
                window.turnstile.execute(turnstileWidget);
            }, true);

            form.addEventListener('submit', async (event) => {
                event.preventDefault();
                if (submitting) {
                    return;
                }
                hideMessage();
                if (!validate()) {
                    trackSubmit(false);
                    return;
                }

                const key = payloadKey();
                if (key === submittedKey) {
                    if (usesStripeStep()) {
                        openStripeStep();
                        return;
                    }
                    showDone('lead');
                    return;
                }

                submitting = true;
                setBusy(true);
                try {
                    await tokenReady;
                    const body = new FormData(form);
                    body.append('action', 'submit_form');
                    const formType = form.getAttribute('data-form-type');
                    if (formType) {
                        body.set('form_type', formType);
                    }
                    const nonce = window.pe_mp_form_ajax && window.pe_mp_form_ajax.nonce;
                    if (nonce) {
                        body.set('form_nonce', nonce);
                    }
                    const response = await fetch(ajaxUrl(), {
                        method: 'POST',
                        body,
                        credentials: 'same-origin',
                        headers: { 'X-Requested-With': 'XMLHttpRequest' },
                    });
                    const data = await response.json();
                    if (!response.ok || !data || !data.success) {
                        const errorText = (data && data.data && data.data.message)
                            ? data.data.message
                            : 'Something went wrong. Please try again.';
                        showMessage(errorText);
                        trackSubmit(false);
                        return;
                    }
                    submittedKey = key;
                    clearTurnstileToken();
                    mintTimeTrap();
                    trackSubmit(true);
                    if (usesStripeStep()) {
                        openStripeStep();
                        return;
                    }
                    showDone('lead');
                } catch (error) {
                    showMessage('Something went wrong. Please try again.');
                    trackSubmit(false);
                } finally {
                    submitting = false;
                    setBusy(false);
                }
            });

            nameField?.addEventListener('input', () => markInvalid(nameField, false));
            emailField?.addEventListener('input', () => markInvalid(emailField, false));
        }

        document.addEventListener('click', (event) => {
            const trigger = event.target.closest?.('[data-reserve-open], [data-join-psystandard-open]');
            if (trigger) {
                if (trigger.tagName === 'A') {
                    event.preventDefault();
                }
                const plan = trigger.getAttribute('data-plan') || '';
                let billing = trigger.getAttribute('data-billing') || '';
                if (!billing && trigger.closest('[data-billing]')) {
                    billing = trigger.closest('[data-billing]').getAttribute('data-billing') || '';
                }
                open({
                    plan,
                    billing,
                    source: sourceFrom(trigger),
                });
                return;
            }

            const back = event.target.closest?.('[data-reserve-back]');
            if (back && modal.contains(back)) {
                showStep(1, true);
                window.setTimeout(() => {
                    if (nameField) {
                        nameField.focus();
                    }
                }, FADE_OUT_MS + 40);
                return;
            }

            const booked = event.target.closest?.('[data-reserve-booked]');
            if (booked && modal.contains(booked)) {
                showDone('button');
                return;
            }

            const closeBtn = event.target.closest?.('[data-reserve-close]');
            if (closeBtn && modal.contains(closeBtn)) {
                requestClose();
            }
        });

        modal.addEventListener('click', (event) => {
            if (event.target === modal) {
                requestClose();
            }
        });

        document.addEventListener('keydown', (event) => {
            if (event.key === 'Escape' && modal.classList.contains('is-open')) {
                requestClose();
            }
        });

        const hash = window.location.hash;
        const hashPlan = PLAN_HASHES[hash] || '';
        const stripeReturn = new URLSearchParams(window.location.search).get('membership_stripe') === '1';
        if (hash === '#reserve' || hash === '#join-psystandard') {
            open({ source: 'hash' });
        } else if (hashPlan && !stripeReturn && document.querySelector('[data-membership-join-modal="wizard"]')) {
            // Plans continue to the Stripe wizard, which only membership pages render.
            open({ plan: hashPlan, billing: 'monthly', source: 'hash' });
        }

        window.showReserveIntroCall = showIntroCall;
        if (window.peMpPendingIntroCall) {
            const pending = window.peMpPendingIntroCall;
            window.peMpPendingIntroCall = false;
            showIntroCall(typeof pending === 'string' ? pending : '');
        }
    },
};

export default ReserveModal;
